import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, FolderTree, Package } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, TableShell, TR, TD, LoadingRows, EmptyState, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function StoreCategories() {
  const [cats, setCats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [renameFor, setRenameFor] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [deleteFor, setDeleteFor] = useState(null);
  const [moveTo, setMoveTo] = useState("");

  const load = useCallback(() => {
    api.get("/store-admin/categories").then((r) => setCats(r.data.categories)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/store-admin/categories", { name });
      toast.success(`Category "${name}" added`);
      setNewName(""); setAdding(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const move = async (i, dir) => {
    const order = cats.map((c) => c.name);
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    setCats(order.map((name, k) => ({ ...cats.find((c) => c.name === name), position: k + 1 })));
    try {
      await api.post("/store-admin/categories/reorder", { order });
      toast.success("Category order saved");
      load();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const rename = async () => {
    const name = renameVal.trim();
    if (!name || name === renameFor.name) { setRenameFor(null); return; }
    setBusy(true);
    try {
      await api.patch("/store-admin/categories/rename", { old: renameFor.name, new: name });
      toast.success("Category updated successfully");
      setRenameFor(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/store-admin/categories/${encodeURIComponent(deleteFor.name)}`, { params: { move_to: moveTo } });
      toast.success(data.moved_products ? `Category deleted — ${data.moved_products} product(s) moved to "${data.move_to}"` : "Category deleted successfully");
      setDeleteFor(null); setMoveTo(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="store-categories">
      <PageHeader title="Menu Categories" subtitle="Add, edit, reorder and delete your store's categories — the order here controls the menu order on your live website.">
        {!adding ? (
          <Button data-testid="add-category-open" onClick={() => setAdding(true)} className="rounded-full font-bold">
            <Plus className="h-4 w-4 mr-1.5" /> Add Category
          </Button>
        ) : (
          <div className="flex gap-2">
            <Input data-testid="add-category-input" value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Starters" autoFocus className="h-11 rounded-full w-48" />
            <Button data-testid="add-category-save" onClick={add} disabled={busy} className="rounded-full font-bold">Add</Button>
            <Button data-testid="add-category-cancel" variant="ghost" onClick={() => { setAdding(false); setNewName(""); }} className="rounded-full">Cancel</Button>
          </div>
        )}
      </PageHeader>

      {!cats ? <LoadingRows /> : cats.length === 0 ? (
        <EmptyState icon={FolderTree} title="No categories yet" body="Add your first category — it appears in your website menu automatically." />
      ) : (
        <TableShell testid="categories-table" head={["Order", "Category", "Products", "Position", "Actions"]}>
          {cats.map((c, i) => (
            <TR key={c.name} testid={`category-row-${i}`}>
              <TD>
                <div className="flex gap-1">
                  <button data-testid={`cat-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} title="Move up"
                    className="h-8 w-8 rounded-full border border-border hover:bg-muted flex items-center justify-center disabled:opacity-30 transition-colors">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button data-testid={`cat-down-${i}`} disabled={i === cats.length - 1} onClick={() => move(i, 1)} title="Move down"
                    className="h-8 w-8 rounded-full border border-border hover:bg-muted flex items-center justify-center disabled:opacity-30 transition-colors">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </TD>
              <TD><p className="font-bold text-sm" data-testid={`cat-name-${i}`}>{c.name}</p></TD>
              <TD>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground" data-testid={`cat-count-${i}`}>
                  <Package className="h-3.5 w-3.5" /> {c.product_count} product{c.product_count === 1 ? "" : "s"}
                </span>
              </TD>
              <TD className="text-xs font-extrabold text-muted-foreground">#{c.position}</TD>
              <TD>
                <div className="flex gap-1.5">
                  <button data-testid={`cat-edit-${i}`} onClick={() => { setRenameFor(c); setRenameVal(c.name); }} title="Edit"
                    className="h-8 w-8 rounded-full border border-border hover:bg-muted flex items-center justify-center transition-colors">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button data-testid={`cat-delete-${i}`} onClick={() => { setDeleteFor(c); setMoveTo(""); }} title="Delete"
                    className="h-8 w-8 rounded-full border border-border hover:bg-destructive/10 text-destructive flex items-center justify-center transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </TD>
            </TR>
          ))}
        </TableShell>
      )}

      {/* Rename dialog */}
      <Dialog open={!!renameFor} onOpenChange={() => setRenameFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit Category</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2" data-testid="rename-category-form">
            <div className="space-y-1.5"><Label className="overline-label">Category name</Label>
              <Input data-testid="rename-category-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && rename()} autoFocus /></div>
            <p className="text-xs text-muted-foreground">Renaming updates {renameFor?.product_count || 0} product(s) and the live website menu automatically — nothing is duplicated.</p>
            <Button data-testid="rename-category-save" onClick={rename} disabled={busy} className="w-full rounded-full font-bold">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Safe delete dialog */}
      <Dialog open={!!deleteFor} onOpenChange={() => setDeleteFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Delete "{deleteFor?.name}"?</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2" data-testid="delete-category-dialog">
            {deleteFor?.product_count > 0 ? (
              <>
                <p className="text-sm text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 font-semibold">
                  This category contains {deleteFor.product_count} product(s). Products are never deleted — choose where they should go:
                </p>
                <div className="space-y-1.5"><Label className="overline-label">Move products to</Label>
                  <select data-testid="delete-move-to" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}
                    className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">General (no category)</option>
                    {cats.filter((c) => c.name !== deleteFor.name).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select></div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">This category is empty. It will be removed from your website menu. Orders and history are never affected.</p>
            )}
            <div className="flex gap-2">
              <Button data-testid="delete-category-confirm" onClick={doDelete} disabled={busy} variant="destructive" className="rounded-full font-bold flex-1">Delete Category</Button>
              <Button data-testid="delete-category-cancel" variant="outline" onClick={() => setDeleteFor(null)} className="rounded-full font-bold">Keep</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
